import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, ShoppingBag, X, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface AddedToCartModalProps {
  isOpen: boolean;
  onClose: () => void;
  productName?: string;
}

export function AddedToCartModal({ isOpen, onClose, productName }: AddedToCartModalProps) {
  const navigate = useNavigate();

  const handleGoToCart = () => {
    onClose();
    navigate('/cart');
  };

  const handleContinueShopping = () => {
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 50 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Success Header with Green Background */}
            <div className="bg-green-500 p-6 text-white relative">
              <button
                onClick={onClose}
                className="absolute top-4 right-4 p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
              
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.1, type: "spring", duration: 0.5 }}
                className="flex items-center justify-center mb-3"
              >
                <div className="p-3 bg-white rounded-full">
                  <CheckCircle className="h-10 w-10 text-green-500" />
                </div>
              </motion.div>
              
              <motion.h2
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-xl font-bold text-center"
              >
                Added to Cart!
              </motion.h2>
            </div>

            {/* Product Details */}
            <div className="p-6">
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
                className="mb-6"
              >
                <p className="text-gray-600 text-center">
                  <span className="font-semibold text-gray-900">{productName || 'Item'}</span> has been added to your cart.
                </p>
              </motion.div>

              {/* Clear Action Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="space-y-3"
              >
                <button
                  onClick={handleGoToCart}
                  className="w-full py-3.5 px-4 bg-brand-600 hover:bg-brand-700 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg hover:shadow-xl"
                >
                  <ShoppingBag className="h-5 w-5" />
                  Go to Cart
                  <ArrowRight className="h-4 w-4" />
                </button>

                <button
                  onClick={handleContinueShopping}
                  className="w-full py-3.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl transition-all"
                >
                  Continue Shopping
                </button>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}